<script setup lang="ts" generic="T extends string">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import useLocale from '../helpers/use-locale';
import DialogCloseButton from '../DialogCloseButton.vue';
import type { ContentTypeOption } from './content-type-option';

const model = defineModel<T>({ required: true });

type Props = {
  title: string;
  options: ContentTypeOption<T>[];
};
const props = defineProps<Props>();

type Emits = {
  (e: 'close'): void;
};
const emit = defineEmits<Emits>();

const { t } = useLocale();
const { smAndDown } = useDisplay();

const selectedOption = computed(() => props.options.find((o) => o.value === model.value));
const color = computed(() => selectedOption.value?.color ?? 'primary');
</script>

<template>
  <div class="d-flex justify-space-between align-center mb-3">
    <v-spacer />
    <div class="d-flex flex-row align-center">
      <div class="text-h5 align-self-center mr-1" data-testid="add-content-dialog-title">
        {{ title }}
      </div>
      <v-select
        v-model="model"
        :items="options"
        max-width="170"
        class="ml-2 dynamic-color-select"
        :class="`select-${color}`"
        :color="color"
        variant="outlined"
        rounded="sm"
        density="compact"
        item-title="name"
        item-value="value"
        hide-details
        :aria-label="t('components.label_filter_content_type')"
        :menu-props="{ style: 'z-index: 2500' }"
      >
        <template #prepend-inner>
          <v-icon size="xs" :color="color" :class="smAndDown ? 'mx-1' : 'mx-2'" :icon="selectedOption?.icon" />
        </template>
      </v-select>
    </div>
    <v-spacer />
    <DialogCloseButton @click="emit('close')" />
  </div>
</template>

<style scoped lang="scss">
// The selection text and the dropdown chevron don't inherit the field's `color`, so they're set here.
// Two fixed variants rather than an interpolated value: scoped :deep() can't take a dynamic colour.
.dynamic-color-select {
  &.select-primary {
    :deep(.v-select__selection),
    :deep(.v-field__append-inner .v-icon) {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }

  &.select-tertiary {
    :deep(.v-select__selection),
    :deep(.v-field__append-inner .v-icon) {
      color: rgb(var(--v-theme-tertiary)) !important;
    }
  }
}

:deep(.svg-inline--fa) {
  // Safari cuts the icon off without this.
  padding-right: 1px;
  padding-left: 1px;
}
</style>
